import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import FilterIcon from '@assets/crm/Filter.svg'
import { useState } from 'react'
import { useAppTheme } from '@/theme/ThemeContext'
import { useCrmReference } from '@/hooks/useCrm'
import type { CrmFilterName, CrmFilterResource } from '@/services/crmService'

type FilterOption = { id: string; label: string }
type FilterKey = Extract<
  CrmFilterName,
  'type' | 'assignee' | 'priority' | 'status'
>

const FILTER_KEYS: FilterKey[] = ['type', 'assignee', 'priority', 'status']

const FilterArea = ({
  accountId,
  onSearch,
}: {
  accountId: string | null
  onSearch: (filters: CrmFilterResource[]) => void
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { data: reference } = useCrmReference(accountId)

  const BG_COLOR = isDarkMode ? '#202B40' : '#FAFBFD'
  const TEXT_COLOR = isDarkMode ? '#FFFFFF' : '#003F61'
  const BORDER_COLOR = isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)'
  const BUTTON_BG = isDarkMode ? '#374151' : '#E3F5FF'
  const INPUT_BG = isDarkMode ? '#111827' : 'inherit'

  const optionsByKey: Record<FilterKey, FilterOption[]> = {
    type: (reference?.types ?? []).map(t => ({
      id: String(t.id),
      label: t.name,
    })),
    assignee: (reference?.assignees ?? []).map(a => ({
      id: String(a.id),
      label: a.name,
    })),
    priority: (reference?.priorities ?? []).map(p => ({
      id: String(p.value),
      label: p.name,
    })),
    status: (reference?.statuses ?? []).map(s => ({
      id: String(s.id),
      label: s.name,
    })),
  }
  const labels: Record<FilterKey, string> = {
    type: 'סוג משימה',
    assignee: 'משוייך ',
    priority: 'עדיפות ',
    status: 'סטטוס',
  }

  const [selected, setSelected] = useState<Record<FilterKey, FilterOption[]>>({
    type: [],
    assignee: [],
    priority: [],
    status: [],
  })

  const handleSearch = () => {
    onSearch(
      FILTER_KEYS.filter(key => selected[key].length > 0).map(key => ({
        name: key,
        value: selected[key].map(option => option.id),
        operator: 'EQUALS',
      }))
    )
  }

  const autoCompleteSx = {
    width: 170,
    '& .MuiInputBase-root': {
      color: TEXT_COLOR,
      bgcolor: INPUT_BG,
    },
    '& .MuiInputLabel-root': {
      color: isDarkMode ? '#9CA3AF' : 'inherit',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? '#374151' : undefined,
    },
  }

  return (
    <Box
      sx={{
        py: '8px',
        px: '16px',
        maxWidth: '100%',
        boxSizing: 'border-box',
        borderRadius: '6.821px',
        border: `1.2px solid ${BORDER_COLOR}`,
        background: BG_COLOR,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          borderBottom: `1px solid ${isDarkMode ? '#374151' : '#E4E7EB'}`,
          py: '8px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: 'fit-content',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Rubik, sans-serif',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
              color: TEXT_COLOR,
            }}
          >
            סינון
          </Typography>
          <img
            src={FilterIcon}
            alt="FilterIcon"
            style={{ filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }}
          />
        </Box>
      </Box>
      <Box dir="rtl" display="flex" flexWrap="wrap" gap={1} py="19px">
        {FILTER_KEYS.map(key => (
          <Autocomplete
            key={key}
            multiple
            limitTags={1}
            disablePortal
            size="small"
            options={optionsByKey[key]}
            value={selected[key]}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.label}
            sx={autoCompleteSx}
            renderInput={params => (
              <TextField {...params} label={labels[key]} />
            )}
            onChange={(_, value) =>
              setSelected(prev => ({ ...prev, [key]: value }))
            }
          />
        ))}
        <Box sx={{ py: '4px', cursor: 'pointer' }}>
          <Box
            sx={{
              display: 'flex',
              width: '78px',
              height: '100%',
              padding: '2px',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '4.5px',
              border: `1px solid ${isDarkMode ? '#374151' : '#E4E7EB'}`,
              background: BUTTON_BG,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: isDarkMode ? '#4B5563' : '#D1EFFF',
              },
            }}
            onClick={handleSearch}
          >
            <Typography
              sx={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '100%',
                color: TEXT_COLOR,
                cursor: 'pointer',
              }}
            >
              חיפוש
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default FilterArea
